import { inferPropertyTag } from '../../../workers/src/properties';

export const BATCH_LIMIT = 100;
export const QUARANTINE_FIELD = '_unsyncable';
export const HOUSEHOLD_FIELD = '_household_id';

export type ListFilters = { month: string; search?: string; member?: string; property?: string; household?: string | null };
export type ListCache = { key: string; rows: any[] } | null;

export function listFilterKey({ month, search, member, property, household }: ListFilters): string {
  return JSON.stringify([month, search || '', member || '', property || '', household || '']);
}

export function serverRowsOnFailure(cache: ListCache, key: string): any[] {
  return cache?.key === key ? cache.rows : [];
}

export function toQueueRecord(tx: any) {
  // Alpine reactive Proxies cannot be structured-cloned by IndexedDB (DataCloneError).
  return JSON.parse(JSON.stringify(tx));
}

export interface QueueStore {
  getAll(): Promise<any[]>;
  put(item: any): Promise<void>;
  delete(id: string): Promise<void>;
}

export type PostBatch = (items: any[]) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<any>;
}>;

export type DrainResult = {
  synced: number;
  quarantinedForeign: number;
  quarantinedInvalid: number;
  remaining: number;
  stoppedAt: number | null;
};

export function syncResultMessages({ synced, remaining, quarantinedForeign, quarantinedInvalid }: Pick<
  DrainResult, 'synced' | 'remaining' | 'quarantinedForeign' | 'quarantinedInvalid'
>): string[] {
  const messages: string[] = [];
  if (synced > 0) messages.push(`已同步 ${synced} 筆離線記錄`);
  if (remaining > 0) messages.push(`還有 ${remaining} 筆未同步`);
  if (quarantinedForeign > 0) messages.push(`有 ${quarantinedForeign} 筆離線記錄屬於其他家庭，無法同步。到「設定」頁最下方點版本文字可下載備份或刪除`);
  if (quarantinedInvalid > 0) messages.push(`有 ${quarantinedInvalid} 筆離線記錄被伺服器退回（分類已不存在或內容不完整），無法同步。到「設定」頁最下方點版本文字可下載備份或刪除`);
  return messages;
}

export function chunk<T>(items: T[], size = BATCH_LIMIT): T[][] {
  if (!Number.isInteger(size) || size <= 0) throw new RangeError('Invalid chunk size');
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size));
  return batches;
}

function belongsToHousehold(item: any, currentHousehold: string | null): boolean {
  return currentHousehold === null
    || item[HOUSEHOLD_FIELD] == null
    || item[HOUSEHOLD_FIELD] === ''
    || item[HOUSEHOLD_FIELD] === currentHousehold;
}

export function splitQueue(items: any[], currentHousehold: string | null): {
  pending: any[]; unsyncable: any[]; otherHousehold: any[];
} {
  const pending: any[] = [];
  const unsyncable: any[] = [];
  const otherHousehold: any[] = [];
  for (const item of items) {
    if (!belongsToHousehold(item, currentHousehold)) otherHousehold.push(item);
    else (Object.prototype.hasOwnProperty.call(item, QUARANTINE_FIELD) ? unsyncable : pending).push(item);
  }
  return { pending, unsyncable, otherHousehold };
}

export function sqliteLike(text: string | null | undefined, pattern: string): boolean {
  if (text == null) return false;
  const foldAscii = (value: string) => {
    let folded = '';
    for (const char of value) {
      folded += char >= 'A' && char <= 'Z' ? String.fromCharCode(char.charCodeAt(0) + 32) : char;
    }
    return folded;
  };
  const textChars = Array.from(foldAscii(text));
  const patternChars = Array.from(foldAscii(pattern));
  let textIndex = 0;
  let patternIndex = 0;
  let percentIndex = -1;
  let retryTextIndex = 0;

  while (textIndex < textChars.length) {
    if (patternChars[patternIndex] === '%') {
      percentIndex = patternIndex++;
      retryTextIndex = textIndex;
    } else if (patternChars[patternIndex] === '_' || patternChars[patternIndex] === textChars[textIndex]) {
      patternIndex++;
      textIndex++;
    } else if (percentIndex !== -1) {
      patternIndex = percentIndex + 1;
      textIndex = ++retryTextIndex;
    } else {
      return false;
    }
  }
  while (patternChars[patternIndex] === '%') patternIndex++;
  return patternIndex === patternChars.length;
}

export function pendingForList(items: any[], {
  month, search, member, property, serverIds, household,
}: {
  month: string;
  search?: string;
  member?: string;
  property?: string;
  serverIds: ReadonlySet<string>;
  household?: string | null;
}): any[] {
  return splitQueue(items, household ?? null).pending
    .map(item => ({
      ...item,
      property_tag: inferPropertyTag(item.subcategory_id, item.type)
        ?? (item.property_tag === '' ? null : (item.property_tag ?? null)),
      _pending: true,
    }))
    .filter(item => (item.date || '').startsWith(`${month}-`)
      && (!search || sqliteLike(item.description, `%${search}%`))
      && (!member || item.created_by === member)
      && (!property || item.property_tag === property)
      && !serverIds.has(item.id));
}

export function mergeForList(serverRows: any[], pendingRows: any[]): any[] {
  return [...serverRows, ...pendingRows].sort((a, b) =>
    b.date.localeCompare(a.date)
    || Number(!!b._pending) - Number(!!a._pending)
    || (a._pending && b._pending
      ? (b.time || '').localeCompare(a.time || '')
        || (b.created_at || '').localeCompare(a.created_at || '')
      : 0),
  );
}

export async function drainQueue(
  store: QueueStore,
  post: PostBatch,
  household: string | null,
  now: () => string = () => new Date().toISOString(),
): Promise<DrainResult> {
  const { pending } = splitQueue(await store.getAll(), household);
  const batches = chunk(pending);
  let synced = 0;
  let quarantinedForeign = 0;
  let quarantinedInvalid = 0;
  let stoppedAt: number | null = null;

  for (const [index, batch] of batches.entries()) {
    let body: any;
    try {
      const response = await post(batch.map(item => {
        const { [QUARANTINE_FIELD]: _unsyncable, [HOUSEHOLD_FIELD]: _household, ...payload } = item;
        return payload;
      }));
      if (!response.ok) {
        stoppedAt = index;
        break;
      }
      body = await response.json();
    } catch {
      stoppedAt = index;
      break;
    }

    if (Array.isArray(body?.results)) {
      // Only this batch may be changed, once per id.
      const batchItems = new Map(batch.map(item => [item.id, item]));
      for (const result of body.results) {
        const item = batchItems.get(result?.id);
        if (!item) continue;
        if (result.status === 'ok') {
          await store.delete(item.id);
          synced++;
          batchItems.delete(item.id);
        } else if (result.status === 'skipped_foreign_id' || result.status === 'skipped_invalid') {
          await store.put({ ...item, [QUARANTINE_FIELD]: { status: result.status, at: now() } });
          if (result.status === 'skipped_foreign_id') quarantinedForeign++;
          else quarantinedInvalid++;
          batchItems.delete(item.id);
        }
      }
    } else {
      stoppedAt = index;
      break;
    }
  }

  return {
    synced, quarantinedForeign, quarantinedInvalid,
    remaining: splitQueue(await store.getAll(), household).pending.length, stoppedAt,
  };
}
